import {
  getCprCrossSectionImagesFromParameter,
  isCprCrossSectionImageModeEnabled,
  type CprCrossSectionImageMode,
  type CprCrossSectionImages,
} from './cprImageResources';

type PoolLike = {
  parameter?: {
    loaded?: boolean;
    data?: unknown;
  };
  centerData?: {
    loaded?: boolean;
    data?: unknown;
  };
  labelData?: {
    loaded?: boolean;
  };
  branchGraphDataset?: {
    loaded?: boolean;
  };
  cprCrossSectionImages?: {
    loaded?: boolean;
    loading?: boolean;
    error?: boolean;
    data?: unknown;
    frontDataAvailability?: ServerFrontDataAvailability;
  };
};

type PanelConfigLike = {
  panelId?: string;
  componentKey?: string;
};


const SERVER_FRONT_DATA_ANALYSIS_TYPES = new Set([
  'plaque',
  'stenosis',
  'fai',
  'isr',
  'aorticValveCalcium',
]);

function isServerFrontDataAnalysisType(analysisType?: string) {
  return !!analysisType && SERVER_FRONT_DATA_ANALYSIS_TYPES.has(analysisType);
}

function isMatchingServerFrontDataManifest(
  manifest: CprCrossSectionImages | null | undefined,
  analysisType?: string,
) {
  if (!manifest?.revision?.startsWith('front-data:')) return false;
  if (!analysisType) return true;
  return manifest.revision.split(':').includes(analysisType);
}


export function shouldForceLegacyCprForFfrLumenEdit(
  analysisType?: string | null,
  editMode?: string | null,
) {
  return analysisType === 'FFR' && editMode === 'lumen';
}

export type ServerFrontDataAvailability =
  | 'unknown'
  | 'available'
  | 'missing';

export function getServerFrontDataAvailability(
  pool: PoolLike | null | undefined,
): ServerFrontDataAvailability {
  const availability = pool?.cprCrossSectionImages?.frontDataAvailability;
  return availability === 'available' || availability === 'missing'
    ? availability
    : 'unknown';
}

export type CprCrossSectionImageManifestStatus =
  | 'disabled'
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

export function getCprCrossSectionImageManifest(
  mode: CprCrossSectionImageMode,
  pool: PoolLike | null | undefined,
): CprCrossSectionImages | null {
  if (!isCprCrossSectionImageModeEnabled(mode)) return null;
  return (
    getCprCrossSectionImagesFromParameter(pool?.cprCrossSectionImages?.data) ??
    getCprCrossSectionImagesFromParameter(pool?.parameter?.data)
  );
}

export function getCprCrossSectionImageManifestStatus(
  mode: CprCrossSectionImageMode,
  pool: PoolLike | null | undefined,
): CprCrossSectionImageManifestStatus {
  if (!isCprCrossSectionImageModeEnabled(mode)) return 'disabled';
  if (getServerFrontDataAvailability(pool) === 'missing') return 'disabled';
  const item = pool?.cprCrossSectionImages;
  if (item?.error) return 'error';
  if (getCprCrossSectionImageManifest(mode, pool)) return 'ready';
  if (item?.loading) return 'loading';
  return 'idle';
}

export function getEffectiveCprCrossSectionImageMode(
  mode: CprCrossSectionImageMode,
  pool: PoolLike | null | undefined,
  requiresImageShell: boolean = true,
): CprCrossSectionImageMode {
  if (!isCprCrossSectionImageModeEnabled(mode) || !requiresImageShell) {
    return 'legacy';
  }
  if (getServerFrontDataAvailability(pool) === 'missing') {
    return 'legacy';
  }

  const manifest = getCprCrossSectionImageManifest(mode, pool);
  if (manifest) return mode;

  const item = pool?.cprCrossSectionImages;
  if (item?.error || (item?.loaded && !item?.loading)) {
    return 'legacy';
  }

  return mode;
}

export function isCprCrossSectionImageShellReady(
  mode: CprCrossSectionImageMode,
  pool: PoolLike | null | undefined,
) {
  const status = getCprCrossSectionImageManifestStatus(mode, pool);
  return status === 'ready';
}

export function isCprCrossSectionMainRenderReady(
  mode: CprCrossSectionImageMode,
  linkedDataReady: boolean,
  imageShellReady: boolean,
  requiresImageShell: boolean = true,
) {
  if (!isCprCrossSectionImageModeEnabled(mode) || !requiresImageShell) {
    return linkedDataReady;
  }
  return linkedDataReady && imageShellReady;
}

export function shouldUseCprCrossSectionImageShellForLayout(
  mode: CprCrossSectionImageMode,
  panelConfigs: PanelConfigLike[] | null | undefined,
) {
  if (!isCprCrossSectionImageModeEnabled(mode)) return false;
  return (panelConfigs ?? []).some(
    (panel) =>
      panel.panelId === 'CPRPanel' ||
      panel.panelId === 'CrossSectionPanel' ||
      panel.componentKey === 'CPR' ||
      panel.componentKey === 'CrossSection',
  );
}

export function shouldDisableCprCrossSectionVtk(
  mode: CprCrossSectionImageMode,
) {
  return isCprCrossSectionImageModeEnabled(mode);
}

export function shouldRequestCprCrossSectionImages(
  mode: CprCrossSectionImageMode,
  pool: PoolLike | null | undefined,
  requiresImageShell: boolean = true,
  analysisType?: string,
) {
  if (!isCprCrossSectionImageModeEnabled(mode)) return false;
  if (!requiresImageShell) return false;
  if (!pool?.parameter?.loaded) return false;
  if (getServerFrontDataAvailability(pool) === 'missing') return false;

  const item = pool.cprCrossSectionImages;
  if (item?.error) return false;

  if (mode === 'server' && isServerFrontDataAnalysisType(analysisType)) {
    const loadedManifest = getCprCrossSectionImagesFromParameter(item?.data);
    if (isMatchingServerFrontDataManifest(loadedManifest, analysisType)) {
      return false;
    }
    const loadingAnalysisType = (item as { requestedAnalysisType?: string } | undefined)
      ?.requestedAnalysisType;
    return !item?.loading || loadingAnalysisType !== analysisType;
  }

  if (item?.loading) return false;
  if (getCprCrossSectionImageManifest(mode, pool)) return false;
  if (item?.loaded) return false;
  return true;
}
